import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { submitConsultation } from "../lib/api";

const KEY = "samora_exit_popup_shown_v1";

const ExitPopup = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", email: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem(KEY)) return;

        let armed = false;
        const armTimer = setTimeout(() => {
            armed = true;
        }, 8000);

        const onMouseLeave = (e) => {
            if (!armed) return;
            if (e.clientY <= 0) {
                setOpen(true);
                sessionStorage.setItem(KEY, "1");
                document.removeEventListener("mouseleave", onMouseLeave);
            }
        };
        document.addEventListener("mouseleave", onMouseLeave);

        // Mobile: after 22s of scrolling / stay
        const mobileTimer = setTimeout(() => {
            if (window.innerWidth < 768 && !sessionStorage.getItem(KEY)) {
                setOpen(true);
                sessionStorage.setItem(KEY, "1");
            }
        }, 22000);

        return () => {
            clearTimeout(armTimer);
            clearTimeout(mobileTimer);
            document.removeEventListener("mouseleave", onMouseLeave);
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) {
            toast.error("Please share your name and phone number.");
            return;
        }
        setSubmitting(true);
        try {
            await submitConsultation({
                name: form.name,
                phone: form.phone,
                email: form.email || undefined,
                source: "exit_popup",
            });
            toast.success("Sam will call you within 24 hours. Thank you.");
            setOpen(false);
        } catch (err) {
            toast.error("Something went wrong. Please try WhatsApp instead.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="exit-popup"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => {
                if (e.target === e.currentTarget) setOpen(false);
            }}
        >
            <div className="relative bg-samora-bone max-w-4xl w-full grid md:grid-cols-2 shadow-2xl">
                <button
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    data-testid="exit-popup-close"
                    className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-samora-ink hover:text-samora-gold transition-colors"
                >
                    <X size={20} />
                </button>
                <div className="hidden md:block">
                    <img
                        src="/generated/quiet-apartment-living.jpg"
                        alt="A quiet, considered living room"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="p-8 md:p-12">
                    <p className="eyebrow mb-4">Before you go</p>
                    <h3 className="font-display text-3xl md:text-4xl leading-tight text-samora-ink mb-4">
                        A 30-minute conversation. No obligation.
                    </h3>
                    <p className="text-samora-text/70 text-sm leading-relaxed mb-8">
                        Sam personally reviews every enquiry. Leave your
                        number, and we'll set up a private consultation for your
                        home.
                    </p>
                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <label className="samora-label">Name</label>
                            <input
                                data-testid="exit-popup-name"
                                className="samora-input"
                                placeholder="Your full name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                            />
                        </div>
                        <div>
                            <label className="samora-label">Phone</label>
                            <input
                                data-testid="exit-popup-phone"
                                className="samora-input"
                                placeholder="+91"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm({ ...form, phone: e.target.value })
                                }
                            />
                        </div>
                        <div>
                            <label className="samora-label">Email (optional)</label>
                            <input
                                data-testid="exit-popup-email"
                                className="samora-input"
                                type="email"
                                placeholder="you@example.com"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="exit-popup-submit"
                            disabled={submitting}
                            className="btn-samora w-full disabled:opacity-60"
                        >
                            {submitting
                                ? "Sending…"
                                : "Request Free Consultation"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ExitPopup;
